import { Tokens } from '@grupoboticario/flora'
import { forwardRef, useState } from 'react'
import { Image, ImageWrapper } from './SimpleImageMagazine.style'
import { createImages } from './helpers'

export interface SimpleImageMagazineProps {
  image?: string
  imageIsZoomed?: boolean
  description: string
  onLoad?: () => void
  onError?: () => void
}

/**
 * Simple version of ImageMagazine implementation, using <img /> ImageElement, without high resolution image support.
 * @see {@link ImageMagazine}
 */
export const SimpleImageMagazine = forwardRef<HTMLDivElement, SimpleImageMagazineProps>(
  (props, ref) => {
    const { image, description, onLoad, onError, imageIsZoomed } = props
    const [isFetched, setIsFetched] = useState(false)
    const [images, setImages] = useState(createImages(image))

    if (!images) {
      return null
    }

    const highResolutionImage = images?.webpHighResolution || images.jpg

    const handleLoad = () => {
      setIsFetched(true)

      onLoad?.()
    }

    const handleError = () => {
      if (images.webp) {
        const currentImages = { jpg: images.jpg, webpHighResolution: images.webpHighResolution }
        setImages(currentImages)
        return
      }
      if (images.webpHighResolution) {
        const currentImages = { jpg: images.jpg }
        setImages(currentImages)
        return
      }
      onError?.()
    }

    return (
      <ImageWrapper ref={ref}>
        <picture>
          {imageIsZoomed ? (
            <source
              srcSet={highResolutionImage.src}
              type={highResolutionImage.type}
              src={highResolutionImage.src}
            />
          ) : (
            <>
              {images.webpDesktop ? (
                <source
                  media={Tokens.media.desktop}
                  srcSet={images.webpDesktop.srcSet}
                  src={images.webpDesktop.src}
                  type={images.webpDesktop.type}
                />
              ) : null}
              {images?.webp ? (
                <source
                  srcSet={images.webp.srcSet}
                  src={images.webp.src}
                  type={images.webp.type}
                />
              ) : null}
              {images?.webpHighResolution ? (
                <source
                  srcSet={images.webpHighResolution.src}
                  src={images.webpHighResolution.src}
                  type={images.webpHighResolution.type}
                />
              ) : null}
            </>
          )}

          <Image
            src={highResolutionImage.src}
            alt={description}
            css={{
              filter: isFetched ? 'none' : 'blur(5px)',
            }}
            onLoad={handleLoad}
            onError={handleError}
          />
        </picture>
      </ImageWrapper>
    )
  },
)
SimpleImageMagazine.displayName = 'SimpleImageMagazine'
